import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../components/Shared/PageHeader'

import SharedButton from '../../components/Shared/SharedButton';
import TextInput from '../../components/Shared/TextInput';
import { CreateNewVendor, updateVendor } from '../../store/slices/PurchaseSlice';
import { FaCheckDouble } from 'react-icons/fa6';

export default function CreateVendor() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const vendor = location?.state?.vendor;
  const { t } = useTranslation();
  const { isLoading } = useSelector(state => state.purchase);

  const initialValues = {
    name: vendor?.name || '',
    name_ar: vendor?.name_ar || '',
    email: vendor?.email || '',
    phone: vendor?.phone || '',
    city: vendor?.city || '',
    country: vendor?.country || '',
    postal_code: vendor?.postal_code || '',
    address: vendor?.address || '',
    vat_no: vendor?.vat_no || '',
    cr_no: vendor?.cr_no || ''
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t('required')),
    name_ar: Yup.string().required(t('required')),
    email: Yup.string().required(t('required')),
    address: Yup.string()
      .required(t('required'))
      .matches(/^(?!\d+$).+$/, t('invalid_address_format')),
    city: Yup.string().required(t('required')),
    country: Yup.string().required(t('required')),
    postal_code: Yup.string().required(t('required')),
    phone: Yup.string().required(t('required')),
    vat_no: Yup.string().required(t('required')),
    cr_no: Yup.string().required(t('required'))
  });

  const onSubmit = async (data, { resetForm }) => {
    try {
      let resultAction;
      if (vendor) {
        resultAction = await dispatch(updateVendor({ id: vendor.id, data }));
        if (updateVendor.fulfilled.match(resultAction)) {
          resetForm();
          navigate(-1);
        }
      } else {
        resultAction = await dispatch(CreateNewVendor(data));
        if (CreateNewVendor.fulfilled.match(resultAction)) {
          resetForm();
          navigate(-1);
        }
      }
    } catch (error) {
      console.error('Error saving vendor:', error);
    }
  };

  return (
    <div className="flex flex-col w-full gap-2 max-h-[calc(100vh-150px)]">
      <PageHeader title={vendor ? t('Edit Vendor') : t('Create Vendor')} backPath={'/manager/vendor'} />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full'>
              <TextInput
                label={t('name')}
                name={'name'}
                placeholder={`${t('enter')} ${t('name')}`}
                error={formik.touched.name && formik.errors.name}
              />

              <TextInput
                label={t('name_ar')}
                name={'name_ar'}
                placeholder={`${t('enter')} ${t('name_ar')}`}
                error={formik.touched.name_ar && formik.errors.name_ar}
              />

              <TextInput
                type={'email'}
                label={t('email')}
                name={'email'}
                placeholder={`${t('enter')} ${t('email')}`}
                error={formik.touched.email && formik.errors.email}
              />

              <TextInput
                type={'tel'}
                label={t('contact_info')}
                name={'phone'}
                placeholder={t('contact_info_placeholder')}
                error={formik.touched.phone && formik.errors.phone}
              />

              <TextInput
                label={t('city')}
                name={'city'}
                placeholder={`${t('enter')} ${t('city')}`}
                error={formik.touched.city && formik.errors.city}
              />

              <TextInput
                label={t('country')}
                name={'country'}
                placeholder={`${t('enter')} ${t('country')}`}
                error={formik.touched.country && formik.errors.country}
              />

              <TextInput
                label={t('postal_code')}
                name={'postal_code'}
                placeholder={`${t('enter')} ${t('postal_code')}`}
                error={formik.touched.postal_code && formik.errors.postal_code}
              />

              <TextInput
                label={t('address')}
                name={'address'}
                placeholder={`${t('enter')} ${t('address')}`}
                error={formik.touched.address && formik.errors.address}
              />

              <TextInput
                label={t('vat_no')}
                name={'vat_no'}
                placeholder={t('vat_no_placeholder')}
                error={formik.touched.vat_no && formik.errors.vat_no}
              />

              <TextInput
                label={t('cr_no')}
                name={'cr_no'}
                placeholder={t('cr_no_placeholder')}
                error={formik.touched.cr_no && formik.errors.cr_no}
              />
            </div>

            <div className='flex justify-end gap-2'>
              <SharedButton
                type='button'
                icon='pi pi-times'
                label={t('Cancel')}
                buttonType='reset'
                onClick={() => {
                  navigate('/manager/vendor');
                }}
              />
              <SharedButton
                type='submit'
                icon={<FaCheckDouble size={12} className='me-2' />}
                loading={isLoading}
                disabled={isLoading}
                label={vendor ? t('Update') : t('Submit')}
                className='submit-btn'
              />
            </div>
          </Form>
        )} 
      </Formik>
    </div>
  );
}